import { IS_CLINICAL_CODING_ENABLED } from '@/components/constants/flags';
import { useAuthToken } from '@/hooks/useAuthToken';
import { getSessionClinicalCodes } from '@/pages/api/scribeAPIV2';
import { type SessionClinicalCodesResponse } from '@heidi/coding/contracts';
import { ConsultNoteStatus, SidePanelState } from '@heidi/schema/ml-scribe';
import { useQuerySessionById } from '@heidi/session/data';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import {
  invalidateCurrentUser,
  useIsReviewingOtherUser,
  useQueryUser,
} from '../useQueryUser';

// This module is the D2 SPLIT (the useQueryUser mixed-shim pattern): the cache
// surface — key + module-scope invalidator — moved to `@heidi/coding`, while
// the HOOK below stays app-side because it reads the Rematch token, composes
// `useQuerySessionById`, and consults review mode (heal: session extraction +
// the auth-SSOT track). Both names are re-exported so every importer and
// `jest.mock` literal is untouched until the Z1 codemod.
import {
  filterSupportedCodeSystems,
  GET_SESSION_CLINICAL_CODES,
  invalidateSesionClinicalCodes,
} from '@heidi/coding/data';

export { GET_SESSION_CLINICAL_CODES, invalidateSesionClinicalCodes };

export function useQuerySessionClinicalCodes(sessionId: string) {
  const token = useAuthToken();
  const isReviewingOtherUser = useIsReviewingOtherUser();
  const { data: session } = useQuerySessionById({
    sessionId,
    select: (data) => {
      if (!data) return undefined;
      return {
        consult_note: data.consult_note,
      };
    },
  });
  const { data: userData } = useQueryUser((data) => {
    return {
      clinicalCodeAutoGenQuota: data.quota?.clinical_code_auto_gen,
      autoOpenCodingSidebar:
        data.session_settings?.default_side_panel_state === SidePanelState.EMBEDDINGS,
    };
  });

  const outOfQuota =
    userData?.clinicalCodeAutoGenQuota === 0 && !userData.autoOpenCodingSidebar;

  const sessionAvailableForEmbedding =
    !!token &&
    !!sessionId &&
    !isReviewingOtherUser &&
    !isEmpty(session?.consult_note?.result) &&
    session?.consult_note.status === ConsultNoteStatus.COMPLETED &&
    !outOfQuota;

  return {
    ...useQuery<SessionClinicalCodesResponse | undefined>({
      queryKey: [GET_SESSION_CLINICAL_CODES, { sessionId }],
      enabled: IS_CLINICAL_CODING_ENABLED() && sessionAvailableForEmbedding,
      queryFn: async () => {
        const { data } = await getSessionClinicalCodes(sessionId);
        void invalidateCurrentUser();
        return filterSupportedCodeSystems(data);
      },
    }),
    disabledFromQuota: outOfQuota,
  };
}
